import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { ApiError } from "@/api/client";
import {
  clearMonitoringOverride,
  fetchMonitoringSchedule,
  setMonitoringOverride,
} from "@/api/endpoints";
import { InfoTip } from "@/components/InfoTip";
import { useAuth } from "@/hooks/useAuth";
import { bucketByGroup, groupLabel } from "@/lib/contaminantGroups";
import { formatDate, formatDueDisplay } from "@/lib/format";
import { TIPS } from "@/lib/glossary";
import type { OverrideWrite, ScheduleRow } from "@/types/api";

const STATUS_PILL: Record<string, string> = {
  done: "pill-ok",
  due: "pill-low",
  overdue: "pill-high",
  unverified: "pill",
};

const STATUS_LABEL: Record<string, string> = {
  done: "Done",
  due: "Due",
  overdue: "Overdue",
  unverified: "Needs info",
};

export function MonitoringScheduleCard({ pwsId }: { pwsId: string }): JSX.Element {
  const auth = useAuth();
  // Platform-scope AND a writing role — a platform *viewer* must not see
  // override controls (the API would 403 them anyway).
  const canEdit = auth.scope === "platform" && auth.role !== "viewer";
  const [mutError, setMutError] = useState<string | null>(null);
  const [groupFilter, setGroupFilter] = useState<string>("all");
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["monitoring-schedule", pwsId],
    queryFn: () => fetchMonitoringSchedule(pwsId),
    enabled: pwsId.length > 0,
  });

  const invalidate = () =>
    void qc.invalidateQueries({ queryKey: ["monitoring-schedule", pwsId] });

  const setOv = useMutation({
    mutationFn: (v: { ruleId: string; body: Partial<OverrideWrite> }) =>
      setMonitoringOverride(pwsId, v.ruleId, v.body),
    onSuccess: () => {
      setMutError(null);
      invalidate();
    },
    onError: (e) =>
      setMutError(e instanceof ApiError ? e.message : "Override failed — try again."),
  });
  const clearOv = useMutation({
    mutationFn: (ruleId: string) => clearMonitoringOverride(pwsId, ruleId),
    onSuccess: () => {
      setMutError(null);
      invalidate();
    },
    onError: (e) =>
      setMutError(e instanceof ApiError ? e.message : "Override failed — try again."),
  });
  const busy = setOv.isPending || clearOv.isPending;

  if (query.isLoading) {
    return (
      <section className="card">
        <h2 className="card-title">Monitoring schedule</h2>
        <div className="skeleton h-32" aria-label="Loading monitoring schedule" />
      </section>
    );
  }
  if (query.error) {
    const msg = query.error instanceof ApiError ? query.error.message : "Failed to load";
    return (
      <section className="card">
        <h2 className="card-title">Monitoring schedule</h2>
        <p className="text-sm text-severity-high">{msg}</p>
      </section>
    );
  }

  const data = query.data!;
  const c = data.classification;
  const counts = data.rows.reduce<Record<string, number>>((acc, r) => {
    acc[r.status] = (acc[r.status] ?? 0) + 1;
    return acc;
  }, {});
  const buckets = bucketByGroup(data.rows, (r) => r.contaminant_group);
  const visibleRows =
    groupFilter === "all"
      ? data.rows
      : data.rows.filter((r) => r.contaminant_group === groupFilter);

  const sourceLabel =
    c.source === "SW"
      ? "Surface / Subpart H"
      : c.source === "GW"
        ? c.source_assumed
          ? "Ground water (assumed)"
          : "Ground water"
        : "Source —";
  const classLine =
    `${c.system_class ?? "Class —"} · ${sourceLabel}` +
    (c.population != null ? ` · pop ${c.population.toLocaleString()}` : "");
  const summary = [
    counts.overdue ? `${counts.overdue} overdue` : null,
    counts.due ? `${counts.due} due` : null,
    counts.unverified ? `${counts.unverified} need info` : null,
    counts.done ? `${counts.done} done` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <section className="card overflow-x-auto">
      <header className="flex items-baseline justify-between mb-1">
        <h2 className="card-title mb-0 inline-flex items-center">
          Monitoring schedule
          <InfoTip {...TIPS.monSchedule} />
        </h2>
        <span className="text-xs text-ink-400">as of {formatDate(data.as_of)}</span>
      </header>
      <p className="text-xs text-ink-400 mb-3">
        {classLine}
        {summary ? ` · ${summary}` : ""}
        <InfoTip {...TIPS.classification} />
      </p>

      {mutError && (
        <p className="text-sm text-severity-high mb-2" role="alert">
          {mutError}
        </p>
      )}
      {data.rows.length === 0 ? (
        <p className="text-sm text-ink-300">No applicable rules for this system yet.</p>
      ) : (
        <>
        <label className="flex items-center gap-2 text-sm text-ink-200 mb-3">
          Group
          <select
            className="input w-auto py-1 text-sm"
            aria-label="Filter by contaminant group"
            value={groupFilter}
            onChange={(e) => setGroupFilter(e.target.value)}
          >
            <option value="all">All groups ({data.rows.length})</option>
            {buckets.map(([g, rows]) => (
              <option key={g} value={g}>
                {groupLabel(g)} ({rows.length})
              </option>
            ))}
          </select>
        </label>
        <table className="w-full text-sm">
          <thead className="text-left text-ink-300 border-b border-ink-700">
            <tr>
              <th className="py-2 pr-4 font-medium">Contaminant</th>
              <th className="py-2 pr-4 font-medium">Limit<InfoTip {...TIPS.monLimit} /></th>
              <th className="py-2 pr-4 font-medium">Frequency<InfoTip {...TIPS.monFrequency} /></th>
              <th className="py-2 pr-4 font-medium">Last sampled<InfoTip {...TIPS.lastSampled} /></th>
              <th className="py-2 pr-4 font-medium">Next due<InfoTip {...TIPS.nextDue} /></th>
              <th className="py-2 pr-4 font-medium">Status<InfoTip {...TIPS.monStatus} /></th>
              {canEdit && <th className="py-2 font-medium">Override<InfoTip {...TIPS.monOverride} /></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-700">
            {visibleRows.map((r) => (
              <ScheduleTableRow
                key={r.rule_id}
                row={r}
                canEdit={canEdit}
                busy={busy}
                onConfirm={() => setOv.mutate({ ruleId: r.rule_id, body: { confirmed: true } })}
                onSetDate={(d) =>
                  setOv.mutate({ ruleId: r.rule_id, body: { manual_last_sampled: d } })
                }
                onSetFrequency={(f) =>
                  setOv.mutate({ ruleId: r.rule_id, body: { manual_frequency: f } })
                }
                onSetNextDue={(d) =>
                  setOv.mutate({ ruleId: r.rule_id, body: { manual_next_due: d } })
                }
                onClear={() => clearOv.mutate(r.rule_id)}
              />
            ))}
          </tbody>
        </table>
        </>
      )}
    </section>
  );
}

interface RowProps {
  row: ScheduleRow;
  canEdit: boolean;
  busy: boolean;
  onConfirm: () => void;
  onSetDate: (d: string) => void;
  onSetFrequency: (f: string) => void;
  onSetNextDue: (d: string) => void;
  onClear: () => void;
}

function ScheduleTableRow({
  row,
  canEdit,
  busy,
  onConfirm,
  onSetDate,
  onSetFrequency,
  onSetNextDue,
  onClear,
}: RowProps): JSX.Element {
  const isManual = row.last_sampled_source === "manual";
  return (
    <tr>
      <td className="py-2 pr-4">
        {row.contaminant_name}
        <span className="block text-xs text-ink-400">{row.applies_to}</span>
      </td>
      <td className="py-2 pr-4 text-ink-300">{row.limit_display ?? "—"}</td>
      <td className="py-2 pr-4 text-ink-300">{row.frequency}</td>
      <td className="py-2 pr-4 text-ink-300 whitespace-nowrap">
        {row.last_sampled ? formatDate(row.last_sampled) : "—"}
        {isManual && <span className="ml-1 text-xs text-ink-400">(manual)</span>}
      </td>
      <td className="py-2 pr-4 text-ink-300 whitespace-nowrap">{formatDueDisplay(row.next_due)}</td>
      <td className="py-2 pr-4">
        <span className={STATUS_PILL[row.status] ?? "pill"} title={row.reason}>
          {STATUS_LABEL[row.status] ?? row.status}
        </span>
      </td>
      {canEdit && (
        <td className="py-2 align-top">
          <div className="flex flex-col gap-1">
            <div className="whitespace-nowrap">
              <input
                type="date"
                className="input text-xs py-0.5 w-32"
                disabled={busy}
                title="Record the last-sampled date"
                onChange={(e) => {
                  if (e.target.value) onSetDate(e.target.value);
                }}
              />
              <button
                type="button"
                className="ml-2 text-xs text-ink-300 underline disabled:opacity-50"
                disabled={busy}
                onClick={onConfirm}
              >
                Confirm
              </button>
              {isManual && (
                <button
                  type="button"
                  className="ml-2 text-xs text-severity-high underline disabled:opacity-50"
                  disabled={busy}
                  onClick={onClear}
                >
                  Clear
                </button>
              )}
            </div>
            <div className="flex items-center gap-1 whitespace-nowrap">
              <input
                key={row.frequency}
                type="text"
                className="input text-xs py-0.5 w-32"
                disabled={busy}
                placeholder="frequency"
                defaultValue={row.frequency}
                title="Override this system's frequency (e.g. a DEP-approved plan)"
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (v && v !== row.frequency) onSetFrequency(v);
                }}
              />
              <input
                type="date"
                className="input text-xs py-0.5 w-32"
                disabled={busy}
                title="Override the next-due date"
                onChange={(e) => {
                  if (e.target.value) onSetNextDue(e.target.value);
                }}
              />
            </div>
          </div>
        </td>
      )}
    </tr>
  );
}
